import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../api';

const FeedbackPage = () => {
    const { user } = useContext(AuthContext);
    const [activeTab, setActiveTab] = useState('create');
    const [myFeedback, setMyFeedback] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        subject: '',
        message: '',
        category: 'question',
        priority: 'medium'
    });

    useEffect(() => {
        if (activeTab === 'history') {
            fetchMyFeedback();
        }
    }, [activeTab]);

    const fetchMyFeedback = async () => {
        try {
            setLoading(true);
            const res = await api.get('/feedback/student');
            // Handle both old format (array) and new paginated format
            const feedbackData = res.data.success && res.data.feedback
                ? res.data.feedback.data
                : res.data;
            setMyFeedback(Array.isArray(feedbackData) ? feedbackData : []);
        } catch (error) {
            console.error('Error fetching feedback:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            await api.post('/feedback', formData);
            setFormData({
                subject: '',
                message: '',
                category: 'question',
                priority: 'medium'
            });
            alert('Câu hỏi/phản hồi đã được gửi thành công!');

            // Refresh feedback history if on that tab
            if (activeTab === 'history') {
                fetchMyFeedback();
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
            alert('Có lỗi xảy ra khi gửi phản hồi: ' + (error.response?.data?.errors?.[0]?.msg || 'Lỗi không xác định'));
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'bg-yellow-100 text-yellow-800',
            in_progress: 'bg-blue-100 text-blue-800',
            resolved: 'bg-green-100 text-green-800',
            closed: 'bg-gray-100 text-gray-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getPriorityColor = (priority) => {
        const colors = {
            low: 'bg-green-100 text-green-700',
            medium: 'bg-yellow-100 text-yellow-700',
            high: 'bg-orange-100 text-orange-700',
            urgent: 'bg-red-100 text-red-700'
        };
        return colors[priority] || 'bg-gray-100 text-gray-700';
    };

    const categoryLabels = {
        question: 'Câu hỏi',
        feedback: 'Phản hồi',
        technical_issue: 'Vấn đề kỹ thuật',
        complaint: 'Khiếu nại',
        suggestion: 'Đề xuất',
        other: 'Khác'
    };

    const priorityLabels = {
        low: 'Thấp',
        medium: 'Trung bình',
        high: 'Cao',
        urgent: 'Khẩn cấp'
    };

    const statusLabels = {
        pending: 'Đang chờ',
        in_progress: 'Đang xử lý',
        resolved: 'Đã giải quyết',
        closed: 'Đã đóng'
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (user?.role !== 'student') {
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 rounded-3xl bg-white/70 p-12 text-center shadow-xl backdrop-blur">
                <h2 className="text-2xl font-semibold text-slate-900">Không có quyền truy cập</h2>
                <p className="text-slate-600">Trang này chỉ dành cho sinh viên.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
                <h1 className="text-3xl font-bold text-slate-900 mb-2">Hỗ trợ & Phản hồi</h1>
                <p className="text-slate-600">Gửi câu hỏi, phản hồi hoặc báo cáo vấn đề để nhận được hỗ trợ từ quản trị viên</p>
            </div>

            <div className="flex flex-wrap gap-2 rounded-3xl bg-white/70 p-4 shadow-xl backdrop-blur">
                <button
                    onClick={() => setActiveTab('create')}
                    className={`inline-flex items-center gap-2 rounded-2xl px-6 py-3 text-sm font-semibold transition ${
                        activeTab === 'create'
                            ? 'bg-indigo-600 text-white shadow-lg'
                            : 'bg-white/50 text-slate-700 hover:bg-white/80'
                    }`}
                >
                    <i className="fas fa-plus" aria-hidden></i>
                    Gửi phản hồi mới
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`inline-flex items-center gap-2 rounded-2xl px-6 py-3 text-sm font-semibold transition ${
                        activeTab === 'history'
                            ? 'bg-indigo-600 text-white shadow-lg'
                            : 'bg-white/50 text-slate-700 hover:bg-white/80'
                    }`}
                >
                    <i className="fas fa-history" aria-hidden></i>
                    Lịch sử phản hồi
                </button>
            </div>

            <div className="rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
                {activeTab === 'create' && (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-semibold text-slate-900">Gửi phản hồi mới</h2>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Loại phản hồi
                                    </label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    >
                                        {Object.entries(categoryLabels).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Mức độ ưu tiên
                                    </label>
                                    <select
                                        name="priority"
                                        value={formData.priority}
                                        onChange={handleChange}
                                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    >
                                        {Object.entries(priorityLabels).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Tiêu đề
                                </label>
                                <input
                                    type="text"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    required
                                    maxLength={200}
                                    placeholder="Nhập tiêu đề ngắn gọn..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    {formData.subject.length}/200 ký tự
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Nội dung chi tiết
                                </label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    maxLength={2000}
                                    rows={6}
                                    placeholder="Mô tả chi tiết vấn đề hoặc câu hỏi của bạn..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 resize-none"
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    {formData.message.length}/2000 ký tự
                                </p>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {loading ? (
                                    <>
                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
                                        Đang gửi...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-paper-plane" aria-hidden></i>
                                        Gửi phản hồi
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                )}

                {activeTab === 'history' && (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-semibold text-slate-900">Lịch sử phản hồi</h2>

                        {loading ? (
                            <div className="text-center py-8">
                                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600"></div>
                                <p className="mt-2 text-sm text-slate-600">Đang tải...</p>
                            </div>
                        ) : myFeedback.length === 0 ? (
                            <div className="text-center py-12 rounded-2xl bg-slate-50">
                                <div className="text-4xl mb-4">📭</div>
                                <h3 className="text-lg font-semibold text-slate-700 mb-2">Chưa có phản hồi nào</h3>
                                <p className="text-slate-500">Bạn chưa gửi câu hỏi hoặc phản hồi nào.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {myFeedback.map((feedback) => (
                                    <div
                                        key={feedback._id}
                                        className="rounded-2xl border border-slate-200 bg-white/90 p-6 shadow-sm hover:shadow-md transition-shadow"
                                    >
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                                                    {feedback.subject}
                                                </h3>
                                                <div className="flex flex-wrap gap-2">
                                                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(feedback.status)}`}>
                                                        {statusLabels[feedback.status]}
                                                    </span>
                                                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getPriorityColor(feedback.priority)}`}>
                                                        {priorityLabels[feedback.priority]}
                                                    </span>
                                                    <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                                                        {categoryLabels[feedback.category]}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="text-right text-sm text-slate-500">
                                                {formatDate(feedback.createdAt)}
                                            </div>
                                        </div>

                                        <div className="rounded-xl bg-slate-50 p-4 mb-4">
                                            <p className="text-sm text-slate-700 leading-relaxed">
                                                {feedback.message}
                                            </p>
                                        </div>

                                        {feedback.adminReply && (
                                            <div className="border-t border-slate-200 pt-4">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100">
                                                        <i className="fas fa-user-shield text-sm text-indigo-600"></i>
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-slate-900">
                                                            Phản hồi từ {feedback.adminReply.repliedBy?.name || 'Quản trị viên'}
                                                        </h4>
                                                        <p className="text-xs text-slate-500">
                                                            {formatDate(feedback.adminReply.repliedAt)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="rounded-xl bg-indigo-50 p-4">
                                                    <p className="text-sm text-slate-700 leading-relaxed">
                                                        {feedback.adminReply.message}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeedbackPage;